import React, { useState, useMemo } from "react";
import AddMemberBeneficiaryTrustedModal from "../../../AddMemberBeneficiaryTrustedModal";
import { getUnmappedMembers } from "../../../../utils/memberLinks";

const getMemberName = (member) => {
  const personalInfo =
    typeof member?.personal_info === "object"
      ? member.personal_info
      : (() => {
          try {
            return JSON.parse(member?.personal_info || "{}");
          } catch {
            return {};
          }
        })();
  const firstName = personalInfo?.first_name || "";
  const lastName = personalInfo?.last_name || "";
  return firstName || lastName
    ? `${firstName} ${lastName}`.trim()
    : `Member ${member?.id || "Unknown"}`;
};

const UnlinkedMembersForm = ({
  householdData,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
  clientToken,
  onDataUpdate,
}) => {
  const accounts = useMemo(() => householdData?.accounts || [], [householdData]);
  const members = useMemo(() => householdData?.members || [], [householdData]);

  const [showModal, setShowModal] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const unmappedMembers = useMemo(
    () => getUnmappedMembers(members, accounts),
    [members, accounts],
  );

  const handleLinkMember = (memberId) => {
    setSelectedMemberId(memberId);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedMemberId(null);
  };

  const handleModalSuccess = (responseData) => {
    setShowModal(false);
    setSelectedMemberId(null);
    if (onDataUpdate && responseData) {
      onDataUpdate(responseData);
    }
  };

  const handleSubmit = (cb) => {
    // Nothing to send — actual linking happens via the modal
    cb({});
  };

  if (unmappedMembers.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All household members have been linked to accounts.</p>
      </div>
    );
  }

  return (
    <div>

      <p className="text-sm text-gray-600 mb-4">
        The following household members are not linked to any account as an owner, beneficiary, or trusted contact.
      </p>

      {unmappedMembers.map((member) => {
        const name = getMemberName(member);
        return (
          <div
            key={member.id}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4"
          >
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
                {name}
              </h3>
              <button
                onClick={() => handleLinkMember(member.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Link to Accounts
              </button>
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next \u2192"}
        </button>
      </div>

      <AddMemberBeneficiaryTrustedModal
        isOpen={showModal}
        onClose={handleModalClose}
        household={householdData}
        newlyCreatedMemberId={selectedMemberId}
        clientToken={clientToken}
        onHouseholdUpdate={handleModalSuccess}
      />
    </div>
  );
};

export default UnlinkedMembersForm;
